import { useEffect, useState, useCallback } from 'react';
import { Truck, Plus, Pencil, Trash2, X, Loader2, AlertCircle, Phone, Mail, Hash, Search, FileText, CreditCard, MapPin, User, Clock, DollarSign, BadgeCheck, Users } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useCompany } from '@/context/CompanyContext';
import { invoiceIntakeService } from '@/lib/invoiceIntakeService';

interface Supplier {
  id: string; company_id: string; supplier_number: number; name: string;
  contact_person: string | null; tax_id: string | null; email: string | null;
  phone: string | null; payment_terms: string | null; currency: 'ILS' | 'USD' | 'EUR';
  address: string | null; notes: string | null; created_at: string; updated_at: string;
}

interface SupplierForm {
  name: string; contact_person: string; tax_id: string; email: string;
  phone: string; payment_terms: string; currency: 'ILS' | 'USD' | 'EUR';
  address: string; notes: string;
}

interface Contact {
  id: string; supplier_id: string; contact_name: string; phone: string | null;
  email: string | null; notify_discrepancies: boolean; notify_credits: boolean;
  notes: string | null; created_at: string; updated_at: string;
}

interface ContactForm {
  id?: string; contact_name: string; phone: string; email: string;
  notify_discrepancies: boolean; notify_credits: boolean; notes: string;
}

const EMPTY_FORM: SupplierForm = {
  name: '', contact_person: '', tax_id: '', email: '',
  phone: '', payment_terms: '', currency: 'ILS', address: '', notes: '',
};

const EMPTY_CONTACT: ContactForm = {
  contact_name: '', phone: '', email: '',
  notify_discrepancies: true, notify_credits: true, notes: '',
};

const CURRENCIES: { value: 'ILS' | 'USD' | 'EUR'; label: string; symbol: string }[] = [
  { value: 'ILS', label: 'שקל ישראלי', symbol: '₪' },
  { value: 'USD', label: 'דולר אמריקאי', symbol: '$' },
  { value: 'EUR', label: 'אירו', symbol: '€' },
];

const PAYMENT_PRESETS = ['שוטף', 'שוטף+30', 'שוטף+45', 'שוטף+60', 'Net 30', 'Net 45', 'Net 60', 'מיידי'];

const sym = (c: string) => CURRENCIES.find(x => x.value === c)?.symbol ?? c;

export function InvoiceIntakeSettingsPage() {
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [form, setForm] = useState<SupplierForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [modalTab, setModalTab] = useState<'general' | 'financial' | 'contacts'>('general');

  const [delTarget, setDelTarget] = useState<Supplier | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactForm, setContactForm] = useState<ContactForm | null>(null);
  const [contactSaving, setContactSaving] = useState(false);

  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchSuppliers = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setError(null);
    const { data, error: err } = await supabase
      .from('inventory_suppliers')
      .select('id,company_id,supplier_number,name,contact_person,tax_id,email,phone,payment_terms,currency,address,notes,created_at,updated_at')
      .eq('company_id', companyId).order('supplier_number');
    if (err) setError(`שגיאה בטעינת ספקים: ${err.message}`);
    else setSuppliers((data as Supplier[]) ?? []);
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const loadContacts = useCallback(async (sid: string) => {
    setContactsLoading(true);
    const { data } = await invoiceIntakeService.getSupplierContacts(sid);
    setContacts((data as Contact[]) ?? []);
    setContactsLoading(false);
  }, []);

  const openAdd = () => {
    setEditing(null); setForm(EMPTY_FORM); setFormError(null);
    setModalTab('general'); setContacts([]); setContactForm(null); setModalOpen(true);
  };

  const openEdit = (s: Supplier) => {
    setEditing(s);
    setForm({
      name: s.name, contact_person: s.contact_person ?? '', tax_id: s.tax_id ?? '',
      email: s.email ?? '', phone: s.phone ?? '', payment_terms: s.payment_terms ?? '',
      currency: s.currency, address: s.address ?? '', notes: s.notes ?? '',
    });
    setFormError(null); setModalTab('general'); setContactForm(null);
    setModalOpen(true); loadContacts(s.id);
  };

  const closeModal = () => { setModalOpen(false); setEditing(null); setForm(EMPTY_FORM); setFormError(null); setContactForm(null); };

  const setField = <K extends keyof SupplierForm>(k: K, v: SupplierForm[K]) => setForm(p => ({ ...p, [k]: v }));

  const validate = (): string | null => {
    if (!form.name.trim()) return 'שם הספק הוא שדה חובה';
    const em = form.email.trim();
    if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) return 'כתובת אימייל אינה תקינה';
    return null;
  };

  const handleSave = async () => {
    if (!companyId) { setFormError('לא נבחרה חברה פעילה'); return; }
    const err = validate();
    if (err) { setFormError(err); return; }
    setSaving(true); setFormError(null);
    const payload = {
      name: form.name.trim(), contact_person: form.contact_person.trim() || null,
      tax_id: form.tax_id.trim() || null, email: form.email.trim() || null,
      phone: form.phone.trim() || null, payment_terms: form.payment_terms.trim() || null,
      currency: form.currency, address: form.address.trim() || null, notes: form.notes.trim() || null,
    };
    try {
      if (editing) {
        const { error: e } = await supabase.from('inventory_suppliers').update(payload).eq('id', editing.id);
        if (e) throw e;
        showToast('ספק עודכן בהצלחה');
      } else {
        const { error: e } = await supabase.from('inventory_suppliers').insert({ company_id: companyId, ...payload });
        if (e) throw e;
        showToast('ספק נוסף בהצלחה');
      }
      closeModal(); fetchSuppliers();
    } catch (e) { setFormError(e instanceof Error ? e.message : 'שגיאה בשמירה'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    const { error: e } = await supabase.from('inventory_suppliers').delete().eq('id', delTarget.id);
    if (e) showToast(e.message, false);
    else { showToast('ספק נמחק'); setDelTarget(null); fetchSuppliers(); }
    setDeleting(false);
  };

  const handleContactSave = async () => {
    if (!contactForm || !editing || !contactForm.contact_name.trim()) return;
    setContactSaving(true);
    const { error: e } = await invoiceIntakeService.upsertSupplierContact({
      ...(contactForm.id ? { id: contactForm.id } : {}),
      supplier_id: editing.id, contact_name: contactForm.contact_name.trim(),
      phone: contactForm.phone.trim() || '', email: contactForm.email.trim() || '',
      notify_discrepancies: contactForm.notify_discrepancies, notify_credits: contactForm.notify_credits,
      notes: contactForm.notes.trim() || '',
    });
    setContactSaving(false);
    if (e) { setFormError((e as Error).message ?? 'שגיאה בשמירה'); return; }
    setContactForm(null); loadContacts(editing.id);
    showToast(contactForm.id ? 'איש קשר עודכן' : 'איש קשר נוסף');
  };

  const handleContactDelete = async (id: string) => {
    if (!window.confirm('האם למחוק איש קשר זה?')) return;
    const { error: e } = await invoiceIntakeService.deleteSupplierContact(id);
    if (e) showToast((e as Error).message ?? 'שגיאה', false);
    else if (editing) { loadContacts(editing.id); showToast('איש קשר נמחק'); }
  };

  const filtered = suppliers.filter(s => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.trim().toLowerCase();
    return s.name?.toLowerCase().includes(q) || s.contact_person?.toLowerCase().includes(q)
      || s.phone?.includes(q) || s.tax_id?.includes(q);
  });

  const TABS: { key: typeof modalTab; label: string }[] = [
    { key: 'general', label: 'פרטים כלליים' },
    { key: 'financial', label: 'פיננסי ותנאים' },
    ...(editing ? [{ key: 'contacts' as const, label: 'אנשי קשר' }] : []),
  ];

  return (
    <>
      <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Truck className="w-5 h-5 text-teal-500" />
            <div>
              <h2 className="text-base font-semibold text-gray-900">ניהול ספקים</h2>
              <p className="text-xs text-gray-500">כרטסות ספקים, תנאי תשלום ואנשי קשר להתראות</p>
            </div>
          </div>
          <button onClick={openAdd} disabled={!companyId}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Plus className="w-4 h-4" />הוסף ספק
          </button>
        </div>

        {!loading && suppliers.length > 0 && (
          <div className="px-6 py-3 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                placeholder="חיפוש לפי שם, איש קשר, טלפון או ח.פ..."
                className="w-full pr-10 pl-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 placeholder:text-gray-400 transition-colors" />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-3 mx-6 my-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" />{error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-14 gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm">טוען ספקים…</span>
          </div>
        ) : suppliers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
            <Truck className="w-10 h-10 text-gray-200" />
            <p className="text-sm font-medium">אין ספקים עדיין</p>
            <p className="text-xs">לחץ על "הוסף ספק" כדי להוסיף את הספק הראשון</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-3 text-right">מס׳</th>
                  <th className="px-4 py-3 text-right">שם הספק</th>
                  <th className="px-4 py-3 text-right">איש קשר</th>
                  <th className="px-4 py-3 text-right">טלפון</th>
                  <th className="px-4 py-3 text-right">אימייל</th>
                  <th className="px-4 py-3 text-right">תנאי תשלום</th>
                  <th className="px-4 py-3 text-right">מטבע</th>
                  <th className="px-4 py-3 text-right w-24">פעולות</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-4">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-50 text-teal-700 text-xs font-bold rounded-lg border border-teal-200 tabular-nums">
                        <Hash className="w-3 h-3" />{s.supplier_number}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 text-xs font-bold shrink-0">
                          {s.name[0]?.toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 leading-tight">{s.name}</p>
                          {s.tax_id && <p className="text-xs text-gray-400 flex items-center gap-0.5"><BadgeCheck className="w-3 h-3 shrink-0" />{s.tax_id}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-gray-600 text-xs">
                      {s.contact_person ? <span className="flex items-center gap-1"><User className="w-3 h-3 text-gray-400 shrink-0" />{s.contact_person}</span> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-4 text-gray-500 text-xs" dir="ltr">
                      {s.phone ? <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-gray-400 shrink-0" />{s.phone}</span> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-4 text-gray-500 text-xs" dir="ltr">
                      {s.email ? <a href={`mailto:${s.email}`} className="flex items-center gap-1 hover:text-blue-600 transition-colors"><Mail className="w-3 h-3 text-gray-400 shrink-0" />{s.email}</a> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-4">
                      {s.payment_terms ? <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-50 text-violet-700 text-xs font-medium rounded-full border border-violet-200"><Clock className="w-3 h-3" />{s.payment_terms}</span> : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full border ${s.currency === 'ILS' ? 'bg-blue-50 text-blue-700 border-blue-200' : s.currency === 'USD' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                        <DollarSign className="w-3 h-3" />{sym(s.currency)} {s.currency}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(s)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="ערוך"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => setDelTarget(s)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="מחק"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {suppliers.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-50 text-xs text-gray-400">{suppliers.length} ספקים</div>
        )}
      </section>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-bold text-gray-900">{editing ? 'עריכת ספק' : 'הוספת ספק חדש'}</h3>
                {editing && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-50 text-teal-600 text-xs font-bold rounded-lg border border-teal-200">
                    <Hash className="w-3 h-3" />{editing.supplier_number}
                  </span>
                )}
              </div>
              <button onClick={closeModal} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex gap-1 px-6 pt-4 shrink-0">
              {TABS.map(t => (
                <button key={t.key} onClick={() => setModalTab(t.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${modalTab === t.key ? 'bg-teal-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              {formError && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />{formError}
                </div>
              )}

              {modalTab === 'general' && (<>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700">שם הספק <span className="text-red-500">*</span></label>
                  <input type="text" value={form.name} onChange={e => setField('name', e.target.value)} placeholder="לדוגמה: ייבוא כהן בע״מ" autoFocus
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-gray-400" />איש קשר <span className="text-xs font-normal text-gray-400">(אופציונלי)</span></label>
                  <input type="text" value={form.contact_person} onChange={e => setField('contact_person', e.target.value)} placeholder="שם נציג הספק"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-gray-400" />טלפון</label>
                    <input type="tel" value={form.phone} onChange={e => setField('phone', e.target.value)} placeholder="03-0000000" dir="ltr"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-gray-400" />אימייל</label>
                    <input type="email" value={form.email} onChange={e => setField('email', e.target.value)} placeholder="supplier@example.com" dir="ltr"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-gray-400" />כתובת <span className="text-xs font-normal text-gray-400">(אופציונלי)</span></label>
                  <input type="text" value={form.address} onChange={e => setField('address', e.target.value)} placeholder="רחוב, עיר, מיקוד"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-gray-400" />הערות <span className="text-xs font-normal text-gray-400">(אופציונלי)</span></label>
                  <textarea value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="מידע נוסף על הספק…" rows={3}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent" />
                </div>
              </>)}

              {modalTab === 'financial' && (<>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5 text-violet-500" />ח.פ / עוסק מורשה <span className="text-xs font-normal text-gray-400">(אופציונלי)</span></label>
                  <input type="text" value={form.tax_id} onChange={e => setField('tax_id', e.target.value)} placeholder="000000000" dir="ltr"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent" />
                  <p className="text-xs text-gray-400">מספר עוסק מורשה / ח.פ של הספק</p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5 text-green-500" />מטבע עסקה</label>
                  <div className="grid grid-cols-3 gap-2">
                    {CURRENCIES.map(c => (
                      <button key={c.value} type="button" onClick={() => setField('currency', c.value)}
                        className={`flex flex-col items-center gap-1 py-3 px-2 rounded-xl border-2 text-sm font-medium transition-all ${form.currency === c.value ? (c.value === 'ILS' ? 'border-blue-500 bg-blue-50 text-blue-700' : c.value === 'USD' ? 'border-green-500 bg-green-50 text-green-700' : 'border-amber-500 bg-amber-50 text-amber-700') : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'}`}>
                        <span className="text-xl font-bold">{c.symbol}</span>
                        <span className="text-xs">{c.value}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-violet-500" />תנאי תשלום <span className="text-xs font-normal text-gray-400">(אופציונלי)</span></label>
                  <div className="flex flex-wrap gap-1.5 pb-1">
                    {PAYMENT_PRESETS.map(p => (
                      <button key={p} type="button" onClick={() => setField('payment_terms', p)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${form.payment_terms === p ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-300 hover:border-violet-400 hover:text-violet-700'}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                  <input type="text" value={form.payment_terms} onChange={e => setField('payment_terms', e.target.value)} placeholder="או הזן תנאים מותאמים אישית…"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent" />
                </div>
                {editing && (
                  <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
                    <Hash className="w-4 h-4 text-gray-400 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-gray-600">מספר ספק (אוטומטי)</p>
                      <p className="text-lg font-bold text-teal-600 tabular-nums">{editing.supplier_number}</p>
                    </div>
                    <p className="text-xs text-gray-400 mr-auto">מספר זה נוצר אוטומטית ואינו ניתן לשינוי</p>
                  </div>
                )}
              </>)}

              {modalTab === 'contacts' && editing && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 p-3 bg-teal-50 border border-teal-200 rounded-xl">
                    <Users className="w-4 h-4 text-teal-600 shrink-0" />
                    <p className="text-xs text-teal-700">ניהול אנשי קשר מרובים לספק — טלפונים, אימיילים והגדרות התראה לכל איש קשר בנפרד.</p>
                  </div>

                  {contactsLoading ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-teal-600 animate-spin" /></div>
                  ) : (<>
                    {contacts.length === 0 && !contactForm && (
                      <div className="text-center py-6 text-gray-400 bg-gray-50 rounded-xl">
                        <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">אין אנשי קשר לספק זה</p>
                        <p className="text-xs mt-1">הוסף אנשי קשר כדי לקבל התראות על חוסרים ובקשות זיכוי</p>
                      </div>
                    )}

                    {contacts.map(c => (
                      <div key={c.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-start justify-between gap-3">
                        <div className="space-y-1 min-w-0">
                          <p className="font-medium text-gray-900 flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-teal-500" />{c.contact_name}</p>
                          {c.phone && <p className="text-xs text-gray-500 flex items-center gap-1" dir="ltr"><Phone className="w-3 h-3" />{c.phone}</p>}
                          {c.email && <p className="text-xs text-gray-500 flex items-center gap-1" dir="ltr"><Mail className="w-3 h-3" />{c.email}</p>}
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {c.notify_discrepancies && <span className="px-2 py-0.5 bg-teal-100 text-teal-800 rounded-full text-xs font-medium">התראות חוסרים</span>}
                            {c.notify_credits && <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">התראות זיכויים</span>}
                          </div>
                          {c.notes && <p className="text-xs text-gray-400 mt-1">{c.notes}</p>}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button onClick={() => setContactForm({ id: c.id, contact_name: c.contact_name, phone: c.phone ?? '', email: c.email ?? '', notify_discrepancies: c.notify_discrepancies, notify_credits: c.notify_credits, notes: c.notes ?? '' })}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => handleContactDelete(c.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}

                    {!contactForm && (
                      <button onClick={() => setContactForm({ ...EMPTY_CONTACT })}
                        className="flex items-center gap-2 w-full justify-center px-4 py-2.5 border-2 border-dashed border-teal-300 text-teal-600 rounded-xl text-sm font-medium hover:bg-teal-50 transition-colors">
                        <Plus className="w-4 h-4" />הוסף איש קשר
                      </button>
                    )}

                    {contactForm && (
                      <div className="bg-teal-50/50 border border-teal-200 rounded-xl p-4 space-y-3">
                        <h4 className="text-sm font-semibold text-gray-800">{contactForm.id ? 'עריכת איש קשר' : 'איש קשר חדש'}</h4>
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-gray-700">שם איש קשר <span className="text-red-500">*</span></label>
                          <input type="text" value={contactForm.contact_name} onChange={e => setContactForm(p => p ? { ...p, contact_name: e.target.value } : p)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium text-gray-700">טלפון</label>
                            <input type="tel" value={contactForm.phone} onChange={e => setContactForm(p => p ? { ...p, phone: e.target.value } : p)} dir="ltr"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent" />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium text-gray-700">אימייל</label>
                            <input type="email" value={contactForm.email} onChange={e => setContactForm(p => p ? { ...p, email: e.target.value } : p)} dir="ltr"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent" />
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input type="checkbox" checked={contactForm.notify_discrepancies} onChange={e => setContactForm(p => p ? { ...p, notify_discrepancies: e.target.checked } : p)}
                              className="rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                            התראות על חוסרים
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input type="checkbox" checked={contactForm.notify_credits} onChange={e => setContactForm(p => p ? { ...p, notify_credits: e.target.checked } : p)}
                              className="rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                            התראות על בקשות זיכוי
                          </label>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-gray-700">הערות</label>
                          <textarea value={contactForm.notes} onChange={e => setContactForm(p => p ? { ...p, notes: e.target.value } : p)} rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent" />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => setContactForm(null)} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">ביטול</button>
                          <button onClick={handleContactSave} disabled={contactSaving || !contactForm.contact_name.trim()}
                            className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors">
                            {contactSaving && <Loader2 className="w-4 h-4 animate-spin" />}שמור
                          </button>
                        </div>
                      </div>
                    )}
                  </>)}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 justify-end px-6 py-4 border-t border-gray-100 shrink-0">
              <button onClick={closeModal} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg transition-colors">ביטול</button>
              <button onClick={handleSave} disabled={saving || !form.name.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? 'שמור שינויים' : 'הוסף ספק'}
              </button>
            </div>
          </div>
        </div>
      )}

      {delTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-xl shrink-0"><Trash2 className="w-5 h-5 text-red-600" /></div>
              <div>
                <h3 className="text-base font-bold text-gray-900">מחיקת ספק</h3>
                <p className="text-sm text-gray-500 mt-1">
                  האם למחוק את הספק{' '}<span className="font-semibold text-gray-800">"{delTarget.name}"</span>{' '}
                  <span className="text-teal-600 font-mono text-xs">#{delTarget.supplier_number}</span>?
                  {' '}פעולה זו אינה ניתנת לביטול.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button onClick={() => setDelTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg transition-colors">ביטול</button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}מחק
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white transition-all ${toast.ok ? 'bg-teal-600' : 'bg-red-600'}`}>
          {toast.msg}
        </div>
      )}
    </>
  );
}
